import { useScrollDepth } from "@/hooks";
import { trackEvent } from "@/lib/dataLayer";
import {
  BigNumbers,
  ClientLogos,
  CookieConsent,
  DemoFormModal,
  FAQ,
  Footer,
  GoogleReviews,
  Header,
  HeroSection,
  ManualVsAutomation,
  MarqueeBanner,
  MetaPartnership,
  PressHighlights,
  RecoveredHighlight,
  Solutions,
  SuccessStories,
  WhatsAppFloatButton,
  WhatsappBlockedSection,
} from "@/pages/landingPage/components";
import { HOME_FAQ_ITEMS } from "@/pages/landingPage/constants/homeFaq";
import { Helmet } from "react-helmet-async";
import { useState } from "react";

const SITE_URL = "https://fidexa.com.br";
const DESCRIPTION =
  "Sistema de cobrança automatizado via WhatsApp para o varejo: cobrança recorrente das parcelas do crediário, recuperação de inadimplentes e menos inadimplência.";

// JSON-LD renderizado inline (no body) — o Helmet não está aplicando tags no
// <head> desta LP, então scripts ld+json vão direto no DOM para garantir que o
// Google leia os dados estruturados. Mesma abordagem do FAQPage em faq.tsx.
// Mesmo `@id` do Organization global do index.html: o Google junta os dois
// numa entidade só, em vez de ler duas empresas "Fidexa". Fundação,
// fundadores e sede espelham a pergunta "Quem está por trás da Fidexa?" da FAQ
// (fontes em .claude/skills/seo/reference/fidexa-empresa.md §2 e §3).
const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: "Fidexa",
  legalName: "Fidexa Serviços de Crédito Ltda",
  foundingDate: "2025-10",
  founder: [
    { "@type": "Person", name: "Bruno Reis" },
    { "@type": "Person", name: "Hudson Ribeiro" },
    { "@type": "Person", name: "Leonardo Azevedo" },
  ],
  address: {
    "@type": "PostalAddress",
    addressLocality: "Limeira",
    addressRegion: "SP",
    addressCountry: "BR",
  },
  url: SITE_URL,
  logo: `${SITE_URL}/og-image.png`,
  description: DESCRIPTION,
  sameAs: [
    "https://www.linkedin.com/company/fidexa-servicos-credito",
    "https://www.instagram.com/fidexa.oficial/",
    "https://www.youtube.com/@Fidexa.Oficiall",
  ],
};

const softwareApplicationSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Fidexa",
  applicationCategory: "BusinessApplication",
  applicationSubCategory:
    "Sistema de cobrança automatizado via WhatsApp e gestão de crediário",
  operatingSystem: "Web",
  url: SITE_URL,
  description: DESCRIPTION,
  inLanguage: "pt-BR",
  offers: {
    "@type": "Offer",
    priceCurrency: "BRL",
    price: "0",
    availability: "https://schema.org/InStock",
    description:
      "Solicite uma demonstração personalizada para sua rede de lojas.",
  },
  provider: {
    "@type": "Organization",
    name: "Fidexa",
    url: SITE_URL,
  },
};

export const LandingPage = () => {
  useScrollDepth();
  const [isDemoOpen, setIsDemoOpen] = useState(false);

  const openDemo = (
    location: "header" | "hero" | "success_case" | "whatsapp_blocked"
  ) => {
    trackEvent({ event: "cta_click_specialist", location });
    setIsDemoOpen(true);
  };

  return (
    <>
      <Helmet>
        <title>Sistema de Cobrança Automatizado via WhatsApp</title>
        <meta name="description" content={DESCRIPTION} />
        <meta
          name="keywords"
          content="sistema de cobrança automatizado, sistema de cobrança automatizado WhatsApp, sistema de cobrança recorrente, sistema de cobrança de inadimplentes, cobrança via WhatsApp, régua de cobrança, crediário varejo"
        />
        <link rel="canonical" href={`${SITE_URL}/`} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Fidexa" />
        <meta
          property="og:title"
          content="Fidexa — Sistema de cobrança automatizado via WhatsApp"
        />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:url" content={`${SITE_URL}/`} />
        <meta property="og:image" content={`${SITE_URL}/og-image.png`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="pt_BR" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Fidexa — Sistema de cobrança automatizado via WhatsApp"
        />
        <meta name="twitter:description" content={DESCRIPTION} />
        <meta name="twitter:image" content={`${SITE_URL}/og-image.png`} />
      </Helmet>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(organizationSchema),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(softwareApplicationSchema),
        }}
      />
      <div className="min-h-screen max-w-screen overflow-hidden bg-white">
        <Header onSpecialistClick={() => openDemo("header")} />
        <main className="flex flex-col gap-20">
          <section
            aria-labelledby="hero-title"
            className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8"
          >
            <HeroSection onCtaClick={() => openDemo("hero")} />
          </section>

          <section>
            <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
              <RecoveredHighlight />
            </div>

            <ClientLogos />
          </section>
          {/* <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <WhatsAppIntegration />
        </section> */}
          <section className="rounded-t-[2rem] bg-black px-4 sm:px-6 lg:px-12">
            <div className="mx-auto max-w-6xl">
              <SuccessStories onCtaClick={() => openDemo("success_case")} />
            </div>
          </section>
          <section className="relative z-10 -mt-28 rounded-t-[2rem] bg-white px-4 pt-28 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-6xl">
              <Solutions />
            </div>
          </section>

          <ManualVsAutomation />

          <section
            aria-labelledby="whatsapp-bloqueado-title"
            className="bg-ds-brand-500 px-4 sm:px-6 lg:px-8"
          >
            <div className="mx-auto max-w-6xl">
              <WhatsappBlockedSection
                onCtaClick={() => openDemo("whatsapp_blocked")}
              />
            </div>
          </section>

          <section className="px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-6xl">
              <BigNumbers />
            </div>
          </section>

          <section className="px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-6xl">
              <PressHighlights />
            </div>
          </section>

          <MarqueeBanner />

          <section
            aria-labelledby="avaliacoes-title"
            className="px-4 sm:px-6 lg:px-8"
          >
            <div className="mx-auto max-w-6xl">
              <GoogleReviews />
            </div>
          </section>

          <section className="flex min-h-[50vh] items-center px-4 sm:px-6 lg:px-8">
            <div className="mx-auto w-full max-w-6xl">
              <MetaPartnership />
            </div>
          </section>
          {/* Feedback dos clientes — oculto por hora */}
          {/* <Testimonials /> */}
          <section className="px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-6xl">
              <FAQ
                items={HOME_FAQ_ITEMS}
                headingOverride="Perguntas frequentes sobre a Fidexa"
              />
            </div>
          </section>
          <Footer />
        </main>
        <WhatsAppFloatButton />
        <CookieConsent />
        <DemoFormModal
          isOpen={isDemoOpen}
          onClose={() => setIsDemoOpen(false)}
        />
      </div>
    </>
  );
};
